import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { projectAPI, taskAPI, milestoneAPI, columnAPI, resourceAPI, riskAPI, documentAPI, changeRequestAPI, qualityCheckAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { exportToExcel } from '../lib/utils';
import {
  GanttChart as GanttIcon,
  Kanban,
  Target,
  Download,
  Upload,
  ArrowLeft,
  LogOut,
  LayoutDashboard,
  Settings,
  Users,
  Activity,
} from 'lucide-react';

import GanttChart from '../components/GanttChart';
import KanbanBoard from '../components/KanbanBoard';
import WBSManager from '../components/WBSManager';
import MilestonesManager from '../components/MilestonesManager';
import ProjectOverview from '../components/ProjectOverview';
import PlanningComponents from '../components/PlanningComponents';
import TeamManager from '../components/TeamManager';
import ActivityLog from '../components/ActivityLog';
import TransferOwnership from '../components/TransferOwnership';

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [project, setProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [columns, setColumns] = useState([]);
  const [resources, setResources] = useState([]);
  const [risks, setRisks] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [changeRequests, setChangeRequests] = useState([]);
  const [qualityChecks, setQualityChecks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  const loadData = useCallback(async () => {
    try {
      const [projectRes, tasksRes, milestonesRes, columnsRes, resourcesRes, risksRes, docsRes, crsRes, qcsRes] = await Promise.all([
        projectAPI.getOne(id),
        taskAPI.getAll(id),
        milestoneAPI.getAll(id),
        columnAPI.getAll(id),
        resourceAPI.getAll(id),
        riskAPI.getAll(id),
        documentAPI.getAll(id),
        changeRequestAPI.getAll(id),
        qualityCheckAPI.getAll(id),
      ]);
      setProject(projectRes.data);
      setTasks(tasksRes.data);
      setMilestones(milestonesRes.data);
      setColumns(columnsRes.data);
      setResources(resourcesRes.data);
      setRisks(risksRes.data);
      setDocuments(docsRes.data);
      setChangeRequests(crsRes.data);
      setQualityChecks(qcsRes.data);
    } catch (error) {
      toast.error('Failed to load project');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleExport = async () => {
    try {
      const res = await projectAPI.export(id);
      exportToExcel(res.data, project?.name || 'project');
      toast.success('Exported to Excel successfully!');
    } catch (error) {
      toast.error('Failed to export');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-full mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard" className="flex items-center gap-2 text-zinc-600 hover:text-zinc-900 transition-colors">
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm">Back</span>
            </Link>
            <div className="h-6 w-px bg-zinc-200" />
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                <GanttIcon className="w-5 h-5 text-white" />
              </div>
              <span className="font-outfit font-bold text-xl text-zinc-900">{project?.name}</span>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="sm"
              onClick={handleExport}
              data-testid="export-excel-btn"
              className="rounded-lg border-zinc-200"
            >
              <Download className="w-4 h-4 mr-2" />
              Export Excel
            </Button>
            <span className="text-sm text-zinc-600">
              {user?.name}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              data-testid="logout-btn"
              className="text-zinc-600 hover:text-zinc-900"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="p-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-white border border-zinc-200 rounded-xl p-1 mb-6 flex-wrap h-auto">
            <TabsTrigger
              value="overview"
              data-testid="overview-tab"
              className="rounded-lg data-[state=active]:bg-zinc-900 data-[state=active]:text-white"
            >
              <LayoutDashboard className="w-4 h-4 mr-2" />
              Overview
            </TabsTrigger>
            <TabsTrigger
              value="gantt"
              data-testid="gantt-tab"
              className="rounded-lg data-[state=active]:bg-zinc-900 data-[state=active]:text-white"
            >
              <GanttIcon className="w-4 h-4 mr-2" />
              Gantt Chart
            </TabsTrigger>
            <TabsTrigger
              value="kanban"
              data-testid="kanban-tab"
              className="rounded-lg data-[state=active]:bg-zinc-900 data-[state=active]:text-white"
            >
              <Kanban className="w-4 h-4 mr-2" />
              Kanban Board
            </TabsTrigger>
            <TabsTrigger
              value="wbs"
              data-testid="wbs-tab"
              className="rounded-lg data-[state=active]:bg-zinc-900 data-[state=active]:text-white"
            >
              <Upload className="w-4 h-4 mr-2" />
              WBS / Tasks
            </TabsTrigger>
            <TabsTrigger
              value="milestones"
              data-testid="milestones-tab"
              className="rounded-lg data-[state=active]:bg-zinc-900 data-[state=active]:text-white"
            >
              <Target className="w-4 h-4 mr-2" />
              Milestones
            </TabsTrigger>
            <TabsTrigger
              value="planning"
              data-testid="planning-tab"
              className="rounded-lg data-[state=active]:bg-zinc-900 data-[state=active]:text-white"
            >
              <Settings className="w-4 h-4 mr-2" />
              Planning
            </TabsTrigger>
            <TabsTrigger
              value="team"
              data-testid="team-tab"
              className="rounded-lg data-[state=active]:bg-zinc-900 data-[state=active]:text-white"
            >
              <Users className="w-4 h-4 mr-2" />
              Team
            </TabsTrigger>
            <TabsTrigger
              value="activity"
              data-testid="activity-tab"
              className="rounded-lg data-[state=active]:bg-zinc-900 data-[state=active]:text-white"
            >
              <Activity className="w-4 h-4 mr-2" />
              Activity
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-0">
            <ProjectOverview
              project={project}
              tasks={tasks}
              milestones={milestones}
              onRefresh={loadData}
            />
          </TabsContent>

          <TabsContent value="gantt" className="mt-0">
            <GanttChart
              projectId={id}
              tasks={tasks}
              milestones={milestones}
              onTaskUpdate={(updatedTask) => {
                setTasks(tasks.map(t => t.id === updatedTask.id ? updatedTask : t));
              }}
              onRefresh={loadData}
            />
          </TabsContent>

          <TabsContent value="kanban" className="mt-0">
            <KanbanBoard
              projectId={id}
              tasks={tasks}
              columns={columns}
              onTaskUpdate={(updatedTask) => {
                setTasks(tasks.map(t => t.id === updatedTask.id ? updatedTask : t));
              }}
              onColumnsUpdate={setColumns}
              onRefresh={loadData}
            />
          </TabsContent>

          <TabsContent value="wbs" className="mt-0">
            <WBSManager
              projectId={id}
              tasks={tasks}
              milestones={milestones}
              onRefresh={loadData}
            />
          </TabsContent>

          <TabsContent value="milestones" className="mt-0">
            <MilestonesManager
              projectId={id}
              tasks={tasks}
              milestones={milestones}
              onRefresh={loadData}
            />
          </TabsContent>

          <TabsContent value="planning" className="mt-0">
            <PlanningComponents
              projectId={id}
              resources={resources}
              risks={risks}
              documents={documents}
              changeRequests={changeRequests}
              qualityChecks={qualityChecks}
              onRefresh={loadData}
            />
          </TabsContent>

          <TabsContent value="team" className="mt-0">
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div />
                <TransferOwnership
                  projectId={id}
                  projectName={project?.name}
                  currentOwner={{ email: project?.owner_email, name: project?.owner_name }}
                  onTransferred={() => loadData()}
                />
              </div>
              <TeamManager
                projectId={id}
                projectName={project?.name}
              />
            </div>
          </TabsContent>

          <TabsContent value="activity" className="mt-0">
            <ActivityLog projectId={id} />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
